import { useReducer } from "react"

export interface UseListStateHandlers<T> {
  setState: React.Dispatch<React.SetStateAction<T[]>>
  append: (...items: T[]) => void
  prepend: (...items: T[]) => void
  insert: (index: number, ...items: T[]) => void
  pop: () => void
  shift: () => void
  apply: (fn: (item: T, index?: number) => T) => void
  applyWhere: (
    condition: (item: T, index: number) => boolean,
    fn: (item: T, index?: number) => T
  ) => void
  remove: (...indices: number[]) => void
  reorder: ({ from, to }: { from: number; to: number }) => void
  swap: ({ from, to }: { from: number; to: number }) => void
  setItem: (index: number, item: T) => void
  setItemProp: <K extends keyof T, U extends T[K]>(
    index: number,
    prop: K,
    value: U
  ) => void
  filter: (fn: (item: T, i: number) => boolean) => void
}

export type UseListState<T> = [T[], UseListStateHandlers<T>]

export type ListAction<T> =
  | { type: "SET_STATE"; payload: T[] }
  | { type: "APPEND"; payload: T[] }
  | { type: "PREPEND"; payload: T[] }
  | { type: "INSERT"; payload: { index: number; items: T[] } }
  | { type: "POP" }
  | { type: "SHIFT" }
  | { type: "APPLY"; payload: (item: T, index?: number) => T }
  | {
      type: "APPLY_WHERE"
      payload: {
        condition: (item: T, index: number) => boolean
        fn: (item: T, index?: number) => T
      }
    }
  | { type: "REMOVE"; payload: number[] }
  | { type: "REORDER"; payload: { from: number; to: number } }
  | { type: "SWAP"; payload: { from: number; to: number } }
  | { type: "SET_ITEM"; payload: { index: number; item: T } }
  | {
      type: "SET_ITEM_PROP"
      payload: { index: number; prop: keyof T; value: T[keyof T] }
    }
  | { type: "FILTER"; payload: (item: T, i: number) => boolean }

function listReducer<T>(state: T[], action: ListAction<T>): T[] {
  switch (action.type) {
    case "SET_STATE":
      return action.payload
    case "APPEND":
      return [...state, ...action.payload]
    case "PREPEND":
      return [...action.payload, ...state]
    case "INSERT":
      return [
        ...state.slice(0, action.payload.index),
        ...action.payload.items,
        ...state.slice(action.payload.index),
      ]
    case "POP":
      return state.slice(0, -1)
    case "SHIFT":
      return state.slice(1)
    case "APPLY":
      return state.map((item, index) => action.payload(item, index))
    case "APPLY_WHERE":
      return state.map((item, index) =>
        action.payload.condition(item, index)
          ? action.payload.fn(item, index)
          : item
      )
    case "REMOVE":
      return state.filter((_, index) => !action.payload.includes(index))
    case "REORDER": {
      const cloned = [...state]
      const item = state[action.payload.from]
      cloned.splice(action.payload.from, 1)
      cloned.splice(action.payload.to, 0, item)
      return cloned
    }
    case "SWAP": {
      const cloned = [...state]
      const fromItem = cloned[action.payload.from]
      const toItem = cloned[action.payload.to]
      cloned.splice(action.payload.to, 1, fromItem)
      cloned.splice(action.payload.from, 1, toItem)
      return cloned
    }
    case "SET_ITEM": {
      const cloned = [...state]
      cloned[action.payload.index] = action.payload.item
      return cloned
    }
    case "SET_ITEM_PROP": {
      const cloned = [...state]
      cloned[action.payload.index] = {
        ...cloned[action.payload.index],
        [action.payload.prop]: action.payload.value,
      }
      return cloned
    }
    case "FILTER":
      return state.filter(action.payload)
    default:
      return state
  }
}

export function useStableListState<T>(
  initialValue: T[] = []
): [T[], React.Dispatch<ListAction<T>>] {
  const [state, dispatch] = useReducer(listReducer<T>, initialValue)

  return [state, dispatch]
}
